import { RotateCw } from "lucide-react";
import type {
  AccessResult,
  DeviceStatus,
  DoorStatus,
  PersonStatus,
  ProvisioningState,
  SyncState,
  SyncSummary,
} from "@/shared/api/types";
import { cn } from "@/shared/ui/cn";
import { Icon } from "@/shared/ui/icon";
import { accessModePresentation, accessViaSentence, type AccessRowFacts } from "@/shared/utils/access-mode";
import {
  accessResultLabel,
  deviceStatusPresentation,
  doorStatusPresentation,
  personStatusPresentation,
  provisioningPresentation,
  syncStatePresentation,
  syncSummaryPresentation,
  type StateTone,
} from "@/shared/utils/labels";
import { StatusDot, type DotShape } from "./StatusDot";

const TONE_CHIP: Record<StateTone, string> = {
  success: "bg-success-soft text-success-fg",
  danger: "bg-danger-soft text-danger-fg",
  warning: "bg-warning-soft text-warning-fg",
  neutral: "bg-neutral-state-soft text-neutral-state-fg",
  accent: "bg-accent-soft text-accent-fg",
  info: "bg-info-soft text-info-fg",
};

/**
 * How loudly a state is said (redesign 2026-09, pass 02). Ordered, and the order is the point:
 *
 *   text  — the ordinary case. Two thirds of an access log is `Granted`, and a tinted chip on every
 *           one of them is nine hundred chips saying "normal", which is exactly what buries the one
 *           row that is not.
 *   soft  — worth noticing: syncing, offline, failed, held open.
 *   solid — a DENIED result and nothing else. It is the loudest thing the product can say, and
 *           there is no second thing worth saying that loudly on the same screen.
 */
export type ChipEmphasis = "text" | "soft" | "solid";

const TONE_TEXT: Record<StateTone, string> = {
  success: "text-success-fg",
  danger: "text-danger-fg",
  warning: "text-warning-fg",
  neutral: "text-fg-muted",
  accent: "text-accent-fg",
  info: "text-info-fg",
};

interface StatusChipProps {
  tone: StateTone;
  label: string;
  emphasis?: ChipEmphasis | undefined;
  /**
   * Let the label wrap instead of ellipsing. Reserved for text that must never be abbreviated — a
   * refusal's reason, a sync failure's cause — because those are the strings a reader came for.
   */
  wrap?: boolean | undefined;
  shape?: DotShape | undefined;
  live?: boolean | undefined;
  /** "Sync failed" style chips carry a retry inside the chip (§6). */
  onRetry?: (() => void) | undefined;
  className?: string | undefined;
}

/** Words + shape + colour, never colour alone (design-system §6). */
export function StatusChip({
  tone,
  label,
  emphasis = "soft",
  wrap = false,
  shape = "filled",
  live = false,
  onRetry,
  className,
}: StatusChipProps) {
  return (
    <span
      className={cn(
        // `w-fit` because a chip that wraps must still be as wide as its words and no wider: without
        // it the denied chip stretches to fill the result column and reads as a banner, which is
        // louder than the one thing we reserved loud for.
        "inline-flex h-(--chip-h) w-fit max-w-full items-center gap-1.5 rounded-sm border border-transparent text-caption font-medium",
        emphasis === "text" ? cn("px-0", TONE_TEXT[tone]) : "px-1.5",
        emphasis === "soft" ? TONE_CHIP[tone] : null,
        // The one solid fill in the system; `AccessResultChip` is the only caller that asks for it.
        emphasis === "solid" ? "bg-danger text-on-accent" : null,
        className,
      )}
    >
      <StatusDot tone={tone} shape={shape} live={live} onSolid={emphasis === "solid"} />
      {/*
        Two lines, never three: the exception row is `--row-h-exception` tall and a third line spills
        out of it. This is the same `clamp-2` contract the table columns declare — a reason wraps
        rather than ellipsing, and the full string is in the inspector when two lines are not enough.
      */}
      <span className={wrap ? "line-clamp-2 min-w-0" : "truncate"}>{label}</span>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="-mr-0.5 inline-flex size-4 items-center justify-center rounded-sm hover:bg-surface/60"
          aria-label={`Retry: ${label}`}
        >
          <Icon icon={RotateCw} size={16} className="size-3" />
        </button>
      ) : null}
    </span>
  );
}

export function DeviceStatusChip({
  status,
  lastSeenAt,
  now,
  className,
}: {
  status: DeviceStatus;
  lastSeenAt: string | null;
  now?: Date | undefined;
  className?: string | undefined;
}) {
  const p = deviceStatusPresentation(status, lastSeenAt, now);
  return <StatusChip tone={p.tone} shape={p.shape} label={p.label} className={className} />;
}

export function ProvisioningChip({
  state,
  className,
}: {
  state: ProvisioningState;
  className?: string | undefined;
}) {
  const p = provisioningPresentation(state);
  return <StatusChip tone={p.tone} shape={p.shape} label={p.label} className={className} />;
}

export function DoorStatusChip({
  status,
  className,
}: {
  status: DoorStatus;
  className?: string | undefined;
}) {
  const p = doorStatusPresentation(status);
  return <StatusChip tone={p.tone} shape={p.shape} label={p.label} className={className} />;
}

/**
 * The result of one access attempt — and the redesign's single most consequential change
 * (pass 02 board 2a).
 *
 * **Granted is no longer a chip.** It is a tick and a word in success ink, because it is what two
 * thirds of every log says and a tinted pill on each of them was the thing burying the refusals. The
 * ink goes to the exception instead: a denial takes the one solid `--danger` fill in the system and
 * its REASON is never abbreviated — "Denied · QR code not recognised" was the most important string
 * on the old log and the only one that truncated.
 */
export function AccessResultChip({
  result,
  errorCode,
  /**
   * True when the list is already filtered to one result. Then that result is the BASELINE, not the
   * exception: fifteen solid fills saying "denied" on a denied-only list is the same failure as nine
   * hundred chips saying "granted" on an unfiltered one, in the other direction. The chip steps down
   * to the soft tint and the filter chip above carries the weight (pass 02, board 3a).
   */
  pinned = false,
  className,
}: {
  result: AccessResult;
  errorCode: number | null;
  pinned?: boolean | undefined;
  className?: string | undefined;
}) {
  const granted = result === "granted";
  return (
    <StatusChip
      tone={granted ? "success" : "danger"}
      emphasis={granted ? "text" : pinned ? "soft" : "solid"}
      wrap={!granted}
      shape={granted ? "check" : "filled"}
      label={accessResultLabel(result, errorCode)}
      // A denial's reason wraps rather than ellipsing — it was the one string the old log truncated —
      // so the chip takes the exception row's height and grows downward, never sideways.
      className={cn(
        granted ? null : "h-auto min-h-(--chip-h) whitespace-normal py-0.5 leading-tight",
        className,
      )}
    />
  );
}

/** One person on one terminal: pending / syncing / synced / failed / removing / removed (M3 §1). */
export function SyncStateChip({
  state,
  onRetry,
  className,
}: {
  state: SyncState;
  onRetry?: (() => void) | undefined;
  className?: string | undefined;
}) {
  const p = syncStatePresentation(state);
  return <StatusChip tone={p.tone} shape={p.shape} label={p.label} onRetry={onRetry} className={className} />;
}

/**
 * M13 §3: why this person is on (or off) this terminal — "On · via Sales", "Off · set here". The
 * wording lives in `access-mode.ts` so the person page, the device page and the phone agree.
 */
export function AccessModeChip({ row, className }: { row: AccessRowFacts; className?: string | undefined }) {
  const p = accessModePresentation(row);
  return (
    <span title={accessViaSentence(row)} className="inline-flex max-w-full">
      <StatusChip tone={p.tone} shape={p.shape} label={p.label} className={className} />
    </span>
  );
}

/** The people table rollup: "Synced 3/3", "Pending 1/3", "Failed 1/3". */
export function SyncSummaryChip({ sync, className }: { sync: SyncSummary; className?: string | undefined }) {
  const p = syncSummaryPresentation(sync);
  const title =
    sync.total === 0
      ? "No terminals to sync to yet"
      : `${sync.synced} synced · ${sync.pending} pending · ${sync.failed} failed · ${sync.total} terminals`;
  return (
    <span title={title} className="inline-flex max-w-full">
      <StatusChip tone={p.tone} shape={p.shape} label={p.label} className={className} />
    </span>
  );
}

export function PersonStatusChip({
  status,
  className,
}: {
  status: PersonStatus;
  className?: string | undefined;
}) {
  const p = personStatusPresentation(status);
  return <StatusChip tone={p.tone} shape={p.shape} label={p.label} className={className} />;
}

export function LiveChip({
  label = "Live",
  className,
}: {
  label?: string | undefined;
  className?: string | undefined;
}) {
  return <StatusChip tone="accent" label={label} live className={className} />;
}
